<template>
  <el-container class="home" direction="vertical">
    <!-- start: header -->
    <el-header class="home-header full-width full-height" v-height="pageSize.header" style="min-width: 900px">
      <!-- start: logo -->
      <div class="home-header-logo" style="min-width: 300px">
        <h1 class="home-header-logo-inner" @click="onClickLogo">
          <slot name="logo">Unidata DMS</slot>
        </h1>
      </div>
      <!-- end: logo -->

      <!-- start: content -->
      <div class="home-header-content flex flex-middle flex-between">
        <!-- start: navs -->
        <el-menu
          class="home-header-content-navs"
          mode="horizontal"
          menu-trigger="hover"
          background-color="#333333"
          text-color="#fff"
          :active-text-color="menuActiveColor"
          :default-active="$route.name"
        >
          <home-navigate-item
            v-for="(navigate, index) in calcNavigates"
            :key="index"
            :route="navigate"
            @node-click="handleMenuItemClick"
          >
          </home-navigate-item>
        </el-menu>
        <!-- end: navs -->

        <!--<i class="home-header-content-icon el-icon-ali-bell"></i>-->
        <header-user-info></header-user-info>
      </div>
      <!-- end: content -->
    </el-header>
    <!-- end: header -->

    <!-- start: main -->
    <el-container
      class="home-main"
      v-height="$store.state.size.heightHomeMain"
      direction="horizontal"
    >
      <!-- start: aside -->
      <el-aside class="home-main-aside full-height" width="21vw" ref="aside">
        <slot name="aside"></slot>
      </el-aside>
      <!-- end: aside -->
      <div v-if=" currentDbType !== 'redis'" class="home-main-edgeLine"
        @mousedown.stop.prevent="moveCross"></div>
      <!-- start: content -->
      <el-main
        class="home-main-content full-height"
        v-loading.lock="$store.state.loading.content"
        :style="{ padding: pageSize.contentAreaPadding + 'px' }"
        style="min-width: 800px"
      >
        <el-tabs
          class="home-main-content-tabs"
          type="card"
          v-model="activeTabName"
          @tab-click="onTabClick"
          @tab-remove="onTabRemove"
        >
          <el-tab-pane
            v-for="tab in openedTabs"
            :key="tab.name"
            :label="tab.title"
            :name="tab.name"
            :closable="tab.name !== indexTab.name"
          >
          </el-tab-pane>
        </el-tabs>

        <div class="home-main-content-container" v-height="$store.state.size.heightHomeContent">
          <transition name="fade-transform" mode="out-in">
            <keep-alive>
              <router-view />
            </keep-alive>
          </transition>
        </div>
      </el-main>
      <!-- end: content -->
    </el-container>
    <!-- end: main -->
  </el-container>
</template>

<script>
import HomeNavigateItem from './HomeNavigateItem';
import { mapGetters, mapActions } from 'vuex';
import { SOURCES_CONFIG, PRIMARY_THEME_COLOR } from '@/config/variables';
import { windowResize } from '@/utils/helpers';
import HeaderUserInfo from '../layouts/HeaderUserInfo';

export default {
  name: 'Home',

  components: {
    HomeNavigateItem,
    HeaderUserInfo
  },

  data() {
    return {
      menuActiveColor: PRIMARY_THEME_COLOR,
      sourcesConfig: SOURCES_CONFIG,
      pageSize: {
        // 与 styles 的 $height-header 部分保持一致
        header: 50,
        contentAreaPadding: 8
      },
    };
  },

  computed: {
    ...mapGetters({
      currentTab: 'tabs/currentTab',
      activeTab: 'tabs/activeTab',
      currentDbType: 'source/currentDbType'
    }),
    activeTabName: {
      get() {
        return this.$store.state.tabs.activeTabName;
      },
      async set(name) {
        await this.setActiveTab(name);
      }
    },
    openedTabs() {
      return this.$store.state.tabs.openedTabs;
    },
    calcNavigates() {
      return this.$store.state.source.navigates;
    },
    indexTab() {
      const currentSourceConfig = this.sourcesConfig[this.$store.state.source.currentSource];
      return currentSourceConfig
        ? {
            name: currentSourceConfig.indexRouteName,
            path: currentSourceConfig.indexRoutePath,
            fullPath: currentSourceConfig.indexRoutePath,
            meta: { title: '首页' }
          }
        : {
            name: '',
            path: '',
            fullPath: '',
            meta: { title: '首页' }
          };
    }
  },

  watch: {
    /**
     * 根据当前路由切换标签页
     */
    async $route(to, from) {
      // 如果当前标签页未被打开，则添加此标签页，再设置激活
      if (!this.currentTab(to.name)) {
        await this.addTab(to);
      }

      // 如果标签页已打开但路径发生变化，则重新添加标签页
      if (from && from.name === to.name && from.fullPath !== to.fullPath) {
        await this.removeTab(to.name);
        await this.addTab(to);
      }

      this.setActiveTab(to.name);
    }
  },

  created() {
    this.$bus.$on('bus_closeTabPane', name => {
      this.onTabRemove(name);
    });
  },

  mounted() {
    //this.$store.state.source.currentSource
    if (this.currentDbType === 'redis') {
      this.$refs.aside.$el.style.width = 0 + 'px';
    }
    this.initTabs();

    this.calcPageSizeInfo();
    windowResize(() => {
      this.calcPageSizeInfo();
    });
  },

  methods: {
    ...mapActions({
      setActiveTab: 'tabs/setActiveTab',
      addTab: 'tabs/addTab',
      removeTab: 'tabs/removeTab'
    }),
    /**
     * 初始化标签页
     * - 必须添加首页
     * - 添加当前路由所对应的页面
     */
    async initTabs() {
      if (this.openedTabs.length === 0) {
        // 如果当前页面不是首页则添加首页标签
        if (this.$route.name !== this.indexTab.name) {
          await this.addTab(this.indexTab);
        }

        // 添加当前路由的标签
        await this.addTab(this.$route);

        // 设置最后一个标签页为激活状态
        this.setActiveTab(this.openedTabs[this.openedTabs.length - 1].name);
      }
    },
    /**
     * 初始化页面高度信息
     */
    calcPageSizeInfo() {
      // 此值要与 style 部分的 $height-main-tabs-header 的值保持一致
      const heightOfTabHeader = 38;
      const windowHeight = window.innerHeight;
      const windowHomeMain = window.innerHeight - this.pageSize.header;
      const windowHomeContent =
        windowHomeMain - this.pageSize.contentAreaPadding * 2 - heightOfTabHeader;

      this.$store.commit('size/STORE_PAGE_HEIGHT_INFO', {
        page: windowHeight,
        homeMain: window.innerHeight - this.pageSize.header,
        homeContent: windowHomeContent
      });
    },
    /**
     * 点击一个标签页时将其激活，并跳转至对应路由
     */
    onTabClick(tab) {
      this.goToView(tab.name);
    },
    /**
     * 移除标签页。如果被移除的标签页就是当前标签页，则设置后面一个标签页激活
     */
    async onTabRemove(name) {
      await this.removeTab(name);

      if (this.activeTabName === name) {
        const targetTab =
          this.openedTabs.length >= 1
            ? this.openedTabs[this.openedTabs.length - 1].name
            : this.indexTab.name;
        this.goToView(targetTab);
      }
    },
    /**
     * 跳转到某一个路由
     */
    async goToView(tabName) {
      if (tabName) {
        await this.setActiveTab(tabName);
      }

      this.$router.push({
        name: this.activeTab.name,
        // 保证每次都携带 db_sign
        query: { db_sign: this.$route.query.db_sign, ...this.activeTab.query },
        fullPath: this.activeTab.fullPath
      });
    },
    onClickLogo() {
      this.$emit('click-logo');
    },
    handleMenuItemClick(route) {
      if (!route.meta.action) {
        this.$router.push({
          name: route.name,
          query: { db_sign: this.$route.query.db_sign, ...this.activeTab.query }
        });
        return;
      }

      this.$emit('nav-action', route.meta.action);
    },
    moveCross(e) {
      let odiv = e.target; // 获取目标元素
      //计算出鼠标相对点击元素的位置,e.clientX获取的是鼠标的位置，OffsetLeft是元素相对于外层元素的位置
      let x = e.clientX - odiv.offsetLeft;
      document.onmousemove = e => {
        // 获取拖拽元素的位置
        let left = e.clientX - x;
        // 把拖拽元素 放到 当前的位置
        if (left <= 190) {
          left = 190;
        } else if (left >= document.documentElement.clientWidth -10) {
          //document.documentElement.clientWidth 屏幕的可视宽度
          left = document.documentElement.clientWidth -10;
        }
        odiv.style.left = left + 'px';
        this.$refs.aside.$el.style.width = left + 'px';
      };
      // 为了防止 火狐浏览器 拖拽阴影问题
      document.onmouseup = () => {
        document.onmousemove = null;
        document.onmouseup = null;
      };
    }
  }
};
</script>

<style lang="scss" scoped>
// 侧边栏宽度
$width-aside: 21vw;

.home {
  background-color: #f2f3f7;
  position: relative;
  height: 100vh;
  overflow: hidden;

  &-header {
    padding-left: 0;
    padding-right: 0;
    background-color: #20242d;
    display: flex;

    &-logo {
      width: $width-aside;
      max-width: 500px;
      display: flex;
      align-items: center;
      padding-left: 20px;

      &-inner {
        cursor: pointer;
        margin: 0;
        padding: 0;
        color: #fff;
        font-weight: normal;
        font-size: 20px;
      }
    }

    &-content {
      flex: 1;
      min-width: 0;
      padding-left: 12px;
      padding-right: 12px;

      &-icon {
        color: #fff;
        font-size: 18px;
        padding-right: 20px;
      }
    }
  }

  &-main {
    min-height: 0;
    min-width: 0;

    &-aside {
      width: $width-aside;
      overflow: hidden;
    }

    &-edgeLine {
      height: 100%;
      width: 10px;
      cursor: w-resize;
      position: absolute;
      left: $width-aside;
      top: 50px;
      z-index: 9999;
    }

    &-content {
      min-height: 0;
      min-width: 0;
      overflow: hidden;
    }
  }
}
</style>

<style lang="scss">
// 顶部导航条高度
$height-header: 50px;
// 标签页页头高度
$height-main-tabs-header: 38px;
// 内容区域边框颜色
$border-color-tab-content: #e4e7ed;

// 导航栏
.home-header-content-navs.el-menu.el-menu--horizontal {
  height: $height-header;

  & > .el-submenu {
    & > .el-submenu__title {
      font-size: 16px;
      height: $height-header;
      line-height: $height-header;
    }
  }

  & > .el-menu-item {
    font-size: 16px;
    height: $height-header;
    line-height: $height-header;
  }
}

.home-main-content {
  &-tabs {
    & > .el-tabs__header {
      height: $height-main-tabs-header;
      margin-bottom: 0;
      border-bottom: 0 !important;

      .el-tabs__nav-wrap {
        .el-tabs__nav-scroll {
          /* 为了让最左侧和最右侧标签有圆角效果 */
          .el-tabs__nav {
            border-radius: 0;
            overflow: hidden;
            border: 0;

            .el-tabs__item {
              border-left: 0;
              border-bottom: 0;
              color: #888;
              height: $height-main-tabs-header;
              line-height: $height-main-tabs-header;

              &.is-active {
                color: #333;
                background-color: #fff;
                font-weight: bold;
              }
            }
          }
        }
      }
    }

    & > .el-tabs__content {
      flex: 1;
      min-height: 0;
      background-color: #fff;

      .el-tab-pane {
        height: 100%;
      }
    }
  }

  &-container {
    // @IMPORTANT 此处必须设置外边框，保证内部的内容不会少 1px 而产生滚动条
    box-sizing: content-box;
    background-color: #fff;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    /* border-right: 1px solid $border-color-tab-content; */
    /* border-bottom: 1px solid $border-color-tab-content; */
    /* border-left: 1px solid $border-color-tab-content; */

    & > div {
      min-height: 100%;
    }
  }
}
</style>
